import React from "react";
import { useClientSideSplitTreatments } from "@leafly-com/split-next";
import { gtmHeadTag } from "@leafly-com/web-utils";
import dynamic from "next/dynamic";
import Head from "next/head";
import Script from "next/script";

import { SnackBarProvider } from "context/SnackBarContext";
import { ToastFrame } from "context/Toast/ToastFrame";
import { getSplitKey } from "utils/split/getSplitKey";

import AgeGate from "components/AgeGate/AgeGate";
import { ModalFrame } from "components/botanic/Modal";
import { WeedfettiFrame } from "components/botanic/Weedfetti";
import Footer from "components/Footer/Footer";
import { GlobalLoadingSpinner } from "components/GlobalLoadingSpinner/GlobalLoadingSpinner";
import { GooglePublisherTagScript } from "components/GooglePublisherTagScript";
import { Header } from "components/Header";

import { AppSideEffects } from "./AppSideEffects";

const ChatwootWidget = dynamic(() => import("components/ChatwootWidget"), {
  ssr: false,
});

type Props = {
  children?: React.ReactNode;
  currentPath?: string;
  disableFixedHeader?: boolean;
  disableHeaderCollapseOnScroll?: boolean;
  hideFooter?: boolean;
  hideHeader?: boolean;
  isAppBannerCookieDismissed?: boolean;
  showMinimalHeader?: boolean;
};

const Layout: React.FC<Props> = ({
  children,
  currentPath,
  disableFixedHeader,
  disableHeaderCollapseOnScroll,
  hideFooter,
  hideHeader,
  isAppBannerCookieDismissed,
  showMinimalHeader,
}) => {
  const { isLoading, treatments } = useClientSideSplitTreatments(
    ["chatwoot_flag"],
    getSplitKey()
  );

  return (
    <WeedfettiFrame>
      {!showMinimalHeader && (
        <Head>
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href="https://public.leafly.com/favicon/apple-touch-icon.png"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href="https://public.leafly.com/favicon/favicon-32x32.png"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href="https://public.leafly.com/favicon/favicon-16x16.png"
          />
          <link
            rel="mask-icon"
            href="https://public.leafly.com/favicon/safari-pinned-tab.svg"
            color="#034638"
          />
          <link
            rel="shortcut icon"
            href="https://public.leafly.com/favicon/favicon.ico"
          />
          <meta name="apple-mobile-web-app-title" content="Leafly" />
          <meta name="application-name" content="Leafly" />
          <meta name="theme-color" content="#034638" />
          <meta property="og:site_name" content="Leafly" key="og:site_name" />
          <meta
            property="fb:admins"
            content="210495295724612"
            key="fb:admins"
          />
          <meta
            key="twitter:card"
            name="twitter:card"
            content={currentPath === "/" ? "app" : "summary"}
          />
          <meta name="twitter:site" content="@Leafly" key="twitter:site" />
          <meta property="og:type" content="website" key="og:type" />
        </Head>
      )}
      <Script
        async
        id="google-tag-manager"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{ __html: gtmHeadTag }}
      />
      <GooglePublisherTagScript />

      {!hideHeader && (
        <Header
          disableFixedPosition={disableFixedHeader}
          disableScrollCollapse={disableHeaderCollapseOnScroll}
          isAppBannerCookieDismissed={isAppBannerCookieDismissed}
          minimal={showMinimalHeader}
        />
      )}
      <AgeGate />
      <AppSideEffects />
      <GlobalLoadingSpinner />

      <ModalFrame>
        <ToastFrame>
          <SnackBarProvider>
            <main>{children}</main>
          </SnackBarProvider>
        </ToastFrame>
      </ModalFrame>

      {!hideFooter && <Footer />}

      {!isLoading && treatments.chatwoot_flag.treatment === "on" && (
        <ChatwootWidget />
      )}
    </WeedfettiFrame>
  );
};

export default Layout;
